/**
 * Upsert Account Relation Use Case
 * Business logic for upserting (insert or update) account-to-community relationships with date ranges
 */

import type { UpsertCommunityAccountRelationResponse } from '../types/communities-types';
import type { CommunitiesRepository } from '../data/communities-repository';

export class UpsertAccountRelationUseCase {
  constructor(private repository: CommunitiesRepository) {}

  async execute(
    communityProfileId: number,
    accountId: number,
    startDate: string,
    expirationDate: string | null
  ): Promise<UpsertCommunityAccountRelationResponse> {
    // Basic validation
    if (!communityProfileId || !accountId) {
      throw new Error('Community profile ID and account ID are required');
    }

    if (!startDate) {
      throw new Error('Start date is required');
    }

    // Validate date format (ISO YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    const dateTimeRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{3})?)?Z?$/;

    if (!dateRegex.test(startDate) && !dateTimeRegex.test(startDate)) {
      throw new Error('Invalid start date format. Expected YYYY-MM-DD or ISO 8601 datetime');
    }

    if (expirationDate && !dateRegex.test(expirationDate) && !dateTimeRegex.test(expirationDate)) {
      throw new Error('Invalid expiration date format. Expected YYYY-MM-DD or ISO 8601 datetime');
    }

    // Validate expiration date is not before start date (same day allowed)
    if (expirationDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const expiration = new Date(expirationDate);
      expiration.setHours(0, 0, 0, 0);

      if (expiration < start) {
        throw new Error('Expiration date cannot be before start date');
      }
    }

    return await this.repository.upsertAccountRelation(
      communityProfileId,
      accountId,
      startDate,
      expirationDate
    );
  }
}
